/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useEffect, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { isValidEmail } from 'utils/helpers';

import { useLogger } from 'hooks/useLogger/useLogger';
import { sendForgotPasswordEmail } from 'services/amplify';

import { SendEmailProps } from './types';

import * as S from '../styles';

const SendEmail: React.FC<SendEmailProps> = ({ onEmailSent }) => {
  const mountedRef = useRef(true);

  const intl = useIntl();
  const logger = useLogger();

  const [email, setEmail] = useState('');
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [sendEmailError, setSendEmailError] = useState(false);
  const [sendEmailLoading, setSendEmailLoading] = useState(false);

  const handleChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSendEmailError(false);
    setEmail(e.target.value);
  };

  const handleSendEmail = async () => {
    setSendEmailLoading(true);
    if (mountedRef.current) {
      try {
        const data = await sendForgotPasswordEmail(email);

        if (data) {
          onEmailSent(email);
        } else {
          setSendEmailError(true);
        }
      } catch (e) {
        setSendEmailError(true);
        logger.error('send login recovery password email', { e });
      }
    }

    setSendEmailLoading(false);
  };

  const handleActionKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const keyCode = e.which || e.key;

    if (keyCode === 13 && !buttonDisabled) {
      e.preventDefault();
      handleSendEmail();
    }
  };

  useEffect(() => {
    if (email && isValidEmail(email)) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
  }, [email]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return (
    <div onKeyDown={handleActionKeyDown} data-testid="sendEmailStep">
      <S.Title>
        <FormattedMessage id="LOGIN.SEND_EMAIL.TITLE" />
      </S.Title>

      <S.LoginEmailInput
        full
        type="email"
        value={email}
        data-testid="sendEmailInput"
        onClean={() => {
          setEmail('');
          setSendEmailError(false);
        }}
        onChange={handleChangeEmail}
        label={intl.formatMessage({
          id: 'LOGIN.SEND_EMAIL.EMAIL.LABEL',
        })}
        error={
          sendEmailError
            ? intl.formatMessage({
                id: 'LOGIN.SEND_EMAIL.EMAIL.ERROR',
              })
            : ''
        }
      />

      <S.ActionButtonContainer>
        <S.ActionButton
          disabled={buttonDisabled}
          onClick={handleSendEmail}
          loading={sendEmailLoading}
          data-testid="sendEmailActionButton"
        >
          <FormattedMessage id="LOGIN.SEND_EMAIL.ACTION.SEND" />
        </S.ActionButton>
      </S.ActionButtonContainer>
    </div>
  );
};

export default SendEmail;
